import { useMemo } from "react";
import { Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Link } from "react-router";

import { PortfolioStatusTag } from "@/views/Portfolio/shared";
import type { SiteTrialEntry } from "@/views/CrossTrialSiteProfile/types";

type TrialsTabProps = {
  trialEntries: SiteTrialEntry[];
  buildUrl: (path?: string) => string;
};

type Row = SiteTrialEntry & {
  enroll_pct: number | null;
};

/** Phase values arrive both bare ("3") and pre-labeled ("Phase 3", "N/A") —
 * label bare numbers, pass everything else through untouched. */
function formatPhase(phase: string | number): string {
  const v = String(phase).trim();
  return /^[0-9]/.test(v) ? `Phase ${v}` : v;
}

export default function InvestigatorTrialsTab({
  trialEntries,
  buildUrl,
}: TrialsTabProps) {
  const rows: Row[] = useMemo(
    () =>
      trialEntries.map((entry) => {
        const enrolled = entry.enrolled ?? 0;
        const target = entry.target ?? 0;
        return {
          ...entry,
          enroll_pct: target > 0 ? Math.round((enrolled / target) * 100) : null,
        };
      }),
    [trialEntries],
  );

  if (rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-gray-400 text-[13px]">
        No trial data available for this investigator.
      </div>
    );
  }

  const columns: ColumnsType<Row> = [
    {
      title: "Trial",
      key: "trial",
      render: (_, row) => (
        <div className="flex flex-col gap-0.5">
          <Link
            to={buildUrl(`trials/${row.trial_id}/sites`)}
            className="text-[13px] font-medium !text-[#1F2933] hover:!text-[#0C98D0]"
          >
            {row.trial_name ?? row.trial_id}
          </Link>
          <span className="text-[11px] text-[#8C8C8C] font-mono">
            {row.trial_id}
          </span>
        </div>
      ),
    },
    {
      title: "Phase",
      key: "phase",
      width: 90,
      align: "center",
      render: (_, row) =>
        row.phase ? (
          <span className="text-[12px] text-[#48636F] whitespace-nowrap">
            {formatPhase(row.phase)}
          </span>
        ) : (
          <span className="text-[12px] text-[#8C8C8C]">—</span>
        ),
    },
    {
      title: "Status",
      key: "status",
      width: 130,
      render: (_, row) => (
        <PortfolioStatusTag status={row.status} color={row.statusColor} />
      ),
    },
    {
      title: "Role",
      key: "role",
      width: 160,
      render: (_, row) => {
        const role = row.role ?? "";
        const isPI = role === "Principal Investigator";
        return role ? (
          <span
            className={`text-[12px] font-medium ${isPI ? "text-[#2B4752]" : "text-[#6B7280]"}`}
          >
            {role}
          </span>
        ) : (
          <span className="text-[12px] text-[#9CA3AF]">—</span>
        );
      },
    },
    {
      title: "Site",
      key: "site",
      width: 200,
      render: (_, row) =>
        row.site_name ? (
          <Tooltip title={row.site_name}>
            <span className="text-[12px] text-[#4B5563] truncate block max-w-[180px]">
              {row.site_name}
            </span>
          </Tooltip>
        ) : (
          <span className="text-[12px] text-[#9CA3AF]">—</span>
        ),
    },
    {
      title: "Sponsor",
      key: "sponsor",
      width: 200,
      render: (_, row) =>
        row.sponsor ? (
          <Tooltip title={row.sponsor}>
            <span className="text-[12px] text-[#4B5563] truncate block max-w-[180px]">
              {row.sponsor}
            </span>
          </Tooltip>
        ) : (
          <span className="text-[12px] text-[#9CA3AF]">—</span>
        ),
    },
    {
      title: "Enrolled",
      key: "enrolled",
      width: 150,
      align: "center",
      render: (_, row) => {
        const enrolled = row.enrolled ?? 0;
        const target = row.target ?? 0;
        if (target === 0)
          return <span className="text-[12px] text-[#9CA3AF]">—</span>;
        return (
          <div className="flex flex-col items-center gap-0.5">
            <span className="text-[13px] font-semibold text-[#1F2933]">
              {enrolled}
              <span className="text-[12px] font-normal text-[#6B7280]">
                {" "}
                / {target}
              </span>
            </span>
            {row.enroll_pct != null && (
              <>
                <div className="w-full h-1.5 rounded-full bg-[#E8EEF2]">
                  <div
                    className="h-full rounded-full bg-[#0C98D0]"
                    style={{ width: `${Math.min(row.enroll_pct, 100)}%` }}
                  />
                </div>
                <span className="text-[11px] text-[#8C8C8C]">
                  {row.enroll_pct}% of target
                </span>
              </>
            )}
          </div>
        );
      },
    },
    {
      title: "Tenure",
      key: "tenure",
      width: 160,
      render: (_, row) => {
        const start = row.tenure_start;
        const end = row.tenure_end;
        if (!start && !end)
          return <span className="text-[12px] text-[#9CA3AF]">—</span>;
        const fmt = (d: string) => d.slice(0, 10);
        return (
          <div className="text-[12px] text-[#4B5563]">
            {start ? fmt(start) : "—"}
            {" → "}
            {end ? fmt(end) : "ongoing"}
          </div>
        );
      },
    },
  ];

  return (
    <div className="rounded-[8px] border border-[#F0F2F4] bg-white shadow-[0_8px_24px_rgba(15,23,42,0.08)] overflow-x-auto">
      <Table<Row>
        dataSource={rows}
        columns={columns}
        rowKey={(r) => `${r.trial_id}-${r.site_id}`}
        size="small"
        pagination={false}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
}
